/**
 * Document library: upload folders, and choose which ones this agent reads.
 *
 * Corpora are a team-wide library, but attachment is per-backend — see
 * ../corpus/useCorpora.ts. The checkbox is therefore "can this agent read
 * this", not "does this exist".
 */

import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import {
  FolderUp, FileText, Trash2, Loader2, AlertTriangle, Check,
} from "lucide-react"
import type { Corpus } from "../corpus/db"
import type { IngestProgress, NamedFile } from "../corpus/ingest"
import { filesFromDataTransfer, filesFromInput, suggestName } from "../corpus/ingest"

export interface CorpusPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  corpora: Corpus[]
  attachedIds: string[]
  ingesting: IngestProgress | null
  // Left out for a role that can only read the documents
  onIngest?: (files: NamedFile[], name: string) => Promise<unknown>
  onRemove?: (id: string) => void
  onToggleAttached: (id: string) => void
}

export function CorpusPanel({
  open, onOpenChange, corpora, attachedIds, ingesting,
  onIngest, onRemove, onToggleAttached,
}: CorpusPanelProps) {
  const [dragging, setDragging] = useState(false)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFiles = async (files: NamedFile[]) => {
    if (!files.length || !onIngest) return
    await onIngest(files, suggestName(files))
  }

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    // Must read entries before awaiting — the DataTransfer is neutered once
    // the handler yields (see filesFromDataTransfer).
    const files = await filesFromDataTransfer(e.dataTransfer)
    await handleFiles(files)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:max-w-[420px]">
        <SheetHeader>
          <SheetTitle className="text-base">Documents</SheetTitle>
          <SheetDescription className="text-xs">
            Drop a folder to give the agent reference material it can search and read.
            Checked corpora are readable by this agent.
          </SheetDescription>
        </SheetHeader>

        {!onIngest && (
          <p className="mt-4 rounded-lg border p-3 text-xs text-muted-foreground">
            Your role can use these documents but not add or remove them.
          </p>
        )}
        {onIngest && <div
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "mt-4 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
            dragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
          )}
        >
          {ingesting ? (
            <div className="space-y-2">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Reading {ingesting.done} of {ingesting.total}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">{ingesting.current}</p>
            </div>
          ) : (
            <>
              <FolderUp className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Drop a folder or files here
              </p>
              <div className="mt-3 flex justify-center gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => folderInputRef.current?.click()}>
                  Choose folder
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => fileInputRef.current?.click()}>
                  Choose files
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">
                Markdown, text, PDF, Word, Excel, CSV, JSON, HTML
              </p>
            </>
          )}
          {/*
            webkitdirectory is the only way to pick a whole folder; it is
            non-standard but supported in every browser this app targets.
          */}
          <input
            ref={folderInputRef}
            type="file"
            // @ts-expect-error non-standard but widely supported
            webkitdirectory=""
            directory=""
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files) void handleFiles(filesFromInput(e.target.files))
              e.target.value = ""
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files) void handleFiles(filesFromInput(e.target.files))
              e.target.value = ""
            }}
          />
        </div>}

        <ScrollArea className="mt-4 h-[calc(100vh-320px)] [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
          {corpora.length === 0 ? (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              No documents uploaded yet.
            </p>
          ) : (
            <div className="space-y-2 pr-3">
              {corpora.map(c => {
                const attached = attachedIds.includes(c.id)
                return (
                  <div key={c.id} className={cn(
                    "rounded border p-2 transition-colors",
                    attached ? "border-primary/40 bg-primary/5" : "border-input",
                  )}>
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => onToggleAttached(c.id)}
                        title={attached ? "Readable by this agent" : "Not readable by this agent"}
                        className={cn(
                          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                          attached ? "border-primary bg-primary text-primary-foreground" : "border-input",
                        )}
                      >
                        {attached && <Check className="h-3 w-3" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium">{c.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          <FileText className="mr-1 inline h-3 w-3" />
                          {c.fileCount} file{c.fileCount === 1 ? "" : "s"} · {Math.max(1, Math.round(c.totalBytes / 1024))} KB
                        </p>
                        {c.skipped.length > 0 && (
                          <p
                            className="mt-0.5 text-[11px] text-amber-600"
                            title={c.skipped.slice(0, 20).map(s => `${s.path}: ${s.reason}`).join("\n")}
                          >
                            <AlertTriangle className="mr-1 inline h-3 w-3" />
                            {c.skipped.length} not readable
                          </p>
                        )}
                      </div>
                      {onRemove && <button
                        type="button"
                        onClick={() => onRemove(c.id)}
                        title="Delete this corpus"
                        aria-label="Delete this corpus"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
